// src/pages/EditStudent.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  IdentificationIcon,
  HeartIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  BookOpenIcon,
  PhotoIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import apiClient from "../api/client";
import toast from "react-hot-toast";
import { FireLoader } from "../components/Loader";

export default function EditStudent() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    };
    checkMobile();
  }, []);

  // Student Information
  const [studentData, setStudentData] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    dateOfBirth: "",
    gender: "",
    nationality: "Sierra Leonean",
    religion: "",
    tribe: "",
    address: "",
    city: "",
    state: "",
    country: "Sierra Leone",
    phone: "",
    email: "",
    admissionNumber: "",
    currentClassId: "",
    academicYearId: "",
    enrollmentDate: "",
    graduationDate: "",
    status: "active",
    medicalConditions: "",
    allergies: "",
    profilePicture: "",
  });

  // Parent/Guardian Information
  const [parentData, setParentData] = useState({
    parentFirstName: "",
    parentLastName: "",
    parentRelationship: "",
    parentEmail: "",
    parentPhone: "",
    parentAddress: "",
    parentOccupation: "",
    parentEmployer: "",
    hasSecondaryParent: false,
    secondaryParentFirstName: "",
    secondaryParentLastName: "",
    secondaryParentRelationship: "",
    secondaryParentEmail: "",
    secondaryParentPhone: "",
    secondaryParentAddress: "",
    secondaryParentOccupation: "",
    secondaryParentEmployer: "",
    emergencyName: "",
    emergencyRelationship: "",
    emergencyPhone: "",
    emergencyAddress: "",
  });

  // Fetch student data
  useEffect(() => {
    fetchStudentData();
    fetchClasses();
    fetchAcademicYears();
  }, [id]);

  const fetchStudentData = async () => {
    try {
      setFetching(true);
      const response = await apiClient.get(`/students/${id}`);
      const student = response.data?.data || response.data;

      if (student) {
        // Parent details are returned by the student API through the linked
        // parent user (parent_user_id). Keep support for the older flat fields
        // as well so existing records and API responses continue to work.
        const parent =
          student.parentUser ||
          student.parent_user ||
          student.parent ||
          student.guardian ||
          {};

        const secondaryParent =
          student.secondaryParent || student.secondary_parent || {};

        setStudentData({
          firstName: student.first_name || student.firstName || "",
          lastName: student.last_name || student.lastName || "",
          middleName: student.middle_name || student.middleName || "",
          dateOfBirth: student.date_of_birth || student.dateOfBirth || "",
          gender: student.gender || "",
          nationality: student.nationality || "Sierra Leonean",
          religion: student.religion || "",
          tribe: student.tribe || "",
          address: student.address || "",
          city: student.city || "",
          state: student.state || "",
          country: student.country || "Sierra Leone",
          phone: student.phone || "",
          email: student.email || "",
          admissionNumber:
            student.admission_number || student.admissionNumber || "",
          currentClassId: student.current_class_id || student.classId || "",
          academicYearId:
            student.academic_year_id || student.academicYearId || "",
          enrollmentDate:
            student.enrollment_date || student.enrollmentDate || "",
          graduationDate:
            student.graduation_date || student.graduationDate || "",
          status: student.status || "active",
          medicalConditions:
            student.medical_conditions || student.medicalConditions || "",
          allergies: student.allergies || "",
          profilePicture: student.profile_picture || "",
        });

        setParentData({
          parentFirstName:
            student.parent_first_name ||
            student.parentFirstName ||
            parent.first_name ||
            parent.firstName ||
            "",
          parentLastName:
            student.parent_last_name ||
            student.parentLastName ||
            parent.last_name ||
            parent.lastName ||
            "",
          parentRelationship:
            student.parent_relationship ||
            student.parentRelationship ||
            parent.relationship ||
            student.emergency_contact_relationship ||
            "",
          parentEmail:
            student.parent_email || student.parentEmail || parent.email || "",
          parentPhone:
            student.parent_phone ||
            student.parentPhone ||
            parent.phone ||
            student.emergency_contact_phone ||
            "",
          parentAddress:
            student.parent_address ||
            student.parentAddress ||
            parent.address ||
            "",
          parentOccupation:
            student.parent_occupation ||
            student.parentOccupation ||
            parent.occupation ||
            "",
          parentEmployer:
            student.parent_employer ||
            student.parentEmployer ||
            parent.employer ||
            "",
          hasSecondaryParent: !!(
            student.secondary_parent_first_name ||
            student.secondaryParentFirstName ||
            secondaryParent.first_name ||
            secondaryParent.firstName
          ),
          secondaryParentFirstName:
            student.secondary_parent_first_name ||
            student.secondaryParentFirstName ||
            secondaryParent.first_name ||
            secondaryParent.firstName ||
            "",
          secondaryParentLastName:
            student.secondary_parent_last_name ||
            student.secondaryParentLastName ||
            secondaryParent.last_name ||
            secondaryParent.lastName ||
            "",
          secondaryParentRelationship:
            student.secondary_parent_relationship ||
            student.secondaryParentRelationship ||
            secondaryParent.relationship ||
            "",
          secondaryParentEmail:
            student.secondary_parent_email ||
            student.secondaryParentEmail ||
            secondaryParent.email ||
            "",
          secondaryParentPhone:
            student.secondary_parent_phone ||
            student.secondaryParentPhone ||
            secondaryParent.phone ||
            "",
          secondaryParentAddress:
            student.secondary_parent_address ||
            student.secondaryParentAddress ||
            secondaryParent.address ||
            "",
          secondaryParentOccupation:
            student.secondary_parent_occupation ||
            student.secondaryParentOccupation ||
            secondaryParent.occupation ||
            "",
          secondaryParentEmployer:
            student.secondary_parent_employer ||
            student.secondaryParentEmployer ||
            secondaryParent.employer ||
            "",
          emergencyName: student.emergency_contact_name || "",
          emergencyRelationship: student.emergency_contact_relationship || "",
          emergencyPhone: student.emergency_contact_phone || "",
          emergencyAddress: student.emergency_contact_address || "",
        });
      }
    } catch (error) {
      console.error("Error fetching student:", error);
      toast.error("Failed to load student data");
    } finally {
      setFetching(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await apiClient.get("/classes", {
        params: { limit: 100, status: "active" },
      });
      const data = response.data?.data || response.data || [];
      setClasses(data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await apiClient.get("/academic-years");
      const yearsData = response.data?.data || response.data || [];
      setAcademicYears(yearsData);
    } catch (error) {
      console.error("Error fetching academic years:", error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, PNG, and WEBP files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () =>
      setStudentData((prev) => ({ ...prev, profilePicture: reader.result }));
    reader.readAsDataURL(file);
    setProfilePictureFile(file);
    toast.success("Profile picture selected");
  };

  const formatClassDisplay = (cls) => {
    if (!cls) return "";
    let display = cls.name ? cls.name.toUpperCase() : "";
    if (cls.faculty && cls.faculty !== "general")
      display += ` (${cls.faculty.toUpperCase()})`;
    return display;
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!studentData.firstName.trim())
        newErrors.firstName = "First name is required";
      if (!studentData.lastName.trim())
        newErrors.lastName = "Last name is required";
      if (!studentData.dateOfBirth)
        newErrors.dateOfBirth = "Date of birth is required";
      if (!studentData.gender) newErrors.gender = "Gender is required";
      if (!studentData.address.trim())
        newErrors.address = "Address is required";
      if (!studentData.phone.trim())
        newErrors.phone = "Phone number is required";
      if (!studentData.email.trim() || !validateEmail(studentData.email))
        newErrors.email = "Valid email is required";
    }

    if (step === 2) {
      if (!studentData.currentClassId)
        newErrors.currentClassId = "Class is required";
      if (!studentData.enrollmentDate)
        newErrors.enrollmentDate = "Enrollment date is required";
    }

    if (step === 4) {
      if (!parentData.parentFirstName.trim())
        newErrors.parentFirstName = "First name is required";
      if (!parentData.parentLastName.trim())
        newErrors.parentLastName = "Last name is required";
      if (!parentData.parentRelationship.trim())
        newErrors.parentRelationship = "Relationship is required";
      if (!parentData.parentPhone.trim())
        newErrors.parentPhone = "Phone number is required";
      if (!parentData.parentAddress.trim())
        newErrors.parentAddress = "Address is required";
      if (
        !parentData.parentEmail.trim() ||
        !validateEmail(parentData.parentEmail)
      )
        newErrors.parentEmail = "Valid email is required";
    }

    if (step === 5) {
      if (!parentData.emergencyName.trim())
        newErrors.emergencyName = "Name is required";
      if (!parentData.emergencyRelationship.trim())
        newErrors.emergencyRelationship = "Relationship is required";
      if (!parentData.emergencyPhone.trim())
        newErrors.emergencyPhone = "Phone number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStudentChange = (e) => {
    const { name, value } = e.target;
    setStudentData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParentData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      toast.error("Please fix the errors before proceeding");
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadProfilePicture = async () => {
    if (!profilePictureFile) return;
    try {
      const formData = new FormData();
      formData.append("profilePicture", profilePictureFile);

      await apiClient.post(`/students/${id}/upload-profile-picture`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile picture updated!");
    } catch (error) {
      toast.error("Profile picture upload failed. You can try again later.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(5)) return;

    setLoading(true);
    try {
      const payload = {
        first_name: studentData.firstName.trim(),
        last_name: studentData.lastName.trim(),
        middle_name: studentData.middleName?.trim() || null,
        date_of_birth: studentData.dateOfBirth,
        gender: studentData.gender.toLowerCase(),
        nationality: studentData.nationality,
        religion: studentData.religion?.trim() || null,
        tribe: studentData.tribe?.trim() || null,
        address: studentData.address.trim(),
        city: studentData.city?.trim() || null,
        state: studentData.state?.trim() || null,
        country: studentData.country,
        phone: studentData.phone.trim(),
        email: studentData.email.trim(),
        current_class_id: parseInt(studentData.currentClassId),
        academic_year_id: parseInt(studentData.academicYearId),
        enrollment_date: studentData.enrollmentDate,
        graduation_date: studentData.graduationDate || null,
        status: studentData.status,
        medical_conditions: studentData.medicalConditions?.trim() || null,
        allergies: studentData.allergies?.trim() || null,
        parent_first_name: parentData.parentFirstName.trim(),
        parent_last_name: parentData.parentLastName.trim(),
        parent_relationship: parentData.parentRelationship.trim(),
        parent_email: parentData.parentEmail.trim(),
        parent_phone: parentData.parentPhone.trim(),
        parent_address: parentData.parentAddress.trim(),
        parent_occupation: parentData.parentOccupation?.trim() || null,
        parent_employer: parentData.parentEmployer?.trim() || null,
        emergency_contact_name: parentData.emergencyName.trim(),
        emergency_contact_phone: parentData.emergencyPhone.trim(),
        emergency_contact_relationship: parentData.emergencyRelationship.trim(),
        emergency_contact_address: parentData.emergencyAddress.trim(),
      };

      if (parentData.hasSecondaryParent) {
        payload.secondary_parent_first_name =
          parentData.secondaryParentFirstName.trim();
        payload.secondary_parent_last_name =
          parentData.secondaryParentLastName.trim();
        payload.secondary_parent_relationship =
          parentData.secondaryParentRelationship.trim();
        payload.secondary_parent_email = parentData.secondaryParentEmail.trim();
        payload.secondary_parent_phone = parentData.secondaryParentPhone.trim();
        payload.secondary_parent_address =
          parentData.secondaryParentAddress.trim();
        payload.secondary_parent_occupation =
          parentData.secondaryParentOccupation?.trim() || null;
        payload.secondary_parent_employer =
          parentData.secondaryParentEmployer?.trim() || null;
      }

      await apiClient.put(`/students/${id}`, payload);

      if (profilePictureFile) await uploadProfilePicture();

      toast.success("Student updated successfully!");
      setTimeout(() => navigate(`/app/students/${id}`), 1500);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update student");
    } finally {
      setLoading(false);
    }
  };

  const getInputClassName = (fieldName) => {
    const base =
      "w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:border-primary-500 focus:ring-1 transition-all text-base";
    return errors[fieldName] ? `${base} border-red-300 bg-red-50` : base;
  };

  const renderStepIndicator = () => {
    const steps = [
      { number: 1, label: "Student Info" },
      { number: 2, label: "Academic" },
      { number: 3, label: "Medical" },
      { number: 4, label: "Parent/Guardian" },
      { number: 5, label: "Emergency" },
    ];

    return (
      <div className="mb-10 overflow-x-auto pb-2">
        <div className="flex items-center min-w-max">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center flex-1 min-w-[70px]">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    currentStep >= step.number
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {currentStep > step.number ? (
                    <CheckCircleIcon className="w-5 h-5" />
                  ) : (
                    step.number
                  )}
                </div>
                <span
                  className={`text-xs mt-2 text-center ${currentStep >= step.number ? "text-primary-600" : "text-gray-400"}`}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-3 ${currentStep > step.number ? "bg-primary-600" : "bg-gray-200"}`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  if (fetching)
    return <FireLoader fullScreen={true} size="lg" text="LOADING STUDENT..." />;

  return (
    <div className="min-h-screen bg-gray-50 py-6 md:py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 flex items-center gap-3">
              <PencilSquareIcon className="w-8 h-8 md:w-10 md:h-10 text-primary-600" />
              Edit Student
            </h1>
            <p className="text-gray-500 mt-1 text-sm md:text-base">
              Update student and guardian information
            </p>
          </div>
          <button
            onClick={() => navigate(`/app/students/${id}`)}
            className="flex items-center gap-2 px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-2xl transition self-start sm:self-auto"
          >
            <XMarkIcon className="w-5 h-5" /> Cancel
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow border border-gray-100 p-5 sm:p-8 md:p-10">
          {renderStepIndicator()}

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* STEP 1 */}
            {currentStep === 1 && (
              <div className="space-y-8">
                <h3 className="text-2xl font-semibold text-gray-800">
                  Personal Information
                </h3>

                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-3xl p-6 md:p-8 text-center">
                  <div className="mx-auto w-28 h-28 md:w-32 md:h-32 rounded-2xl overflow-hidden border-4 border-white shadow mb-6 bg-white">
                    {studentData.profilePicture ? (
                      <img
                        src={studentData.profilePicture}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <PhotoIcon className="w-14 h-14 md:w-16 md:h-16 text-gray-300" />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <label className="cursor-pointer flex-1 sm:flex-none">
                      <div className="px-6 py-3 bg-white border border-gray-300 rounded-2xl hover:bg-gray-50 font-medium text-sm">
                        Change Photo
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    JPG, PNG, WEBP • Max 5MB
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="firstName"
                        value={studentData.firstName}
                        onChange={handleStudentChange}
                        className={getInputClassName("firstName")}
                        placeholder="John"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="lastName"
                        value={studentData.lastName}
                        onChange={handleStudentChange}
                        className={getInputClassName("lastName")}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Middle Name
                    </label>
                    <input
                      type="text"
                      name="middleName"
                      value={studentData.middleName}
                      onChange={handleStudentChange}
                      className={getInputClassName("middleName")}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={studentData.dateOfBirth}
                        onChange={handleStudentChange}
                        className={getInputClassName("dateOfBirth")}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="gender"
                      value={studentData.gender}
                      onChange={handleStudentChange}
                      className={getInputClassName("gender")}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Nationality
                    </label>
                    <input
                      type="text"
                      name="nationality"
                      value={studentData.nationality}
                      onChange={handleStudentChange}
                      className={getInputClassName("nationality")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Religion
                    </label>
                    <input
                      type="text"
                      name="religion"
                      value={studentData.religion}
                      onChange={handleStudentChange}
                      className={getInputClassName("religion")}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPinIcon className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                    <textarea
                      name="address"
                      value={studentData.address}
                      onChange={handleStudentChange}
                      rows="3"
                      className={`${getInputClassName("address")} pl-11`}
                      placeholder="Full residential address"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={studentData.city}
                      onChange={handleStudentChange}
                      className={getInputClassName("city")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={studentData.state}
                      onChange={handleStudentChange}
                      className={getInputClassName("state")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={studentData.country}
                      onChange={handleStudentChange}
                      className={getInputClassName("country")}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={studentData.phone}
                        onChange={handleStudentChange}
                        className={getInputClassName("phone")}
                        placeholder="+232 76 123 456"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={studentData.email}
                        onChange={handleStudentChange}
                        className={getInputClassName("email")}
                        placeholder="student@example.com"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other steps (2-5) follow the same clean pattern as AddStudent */}
            {/* STEP 2 */}
            {currentStep === 2 && (
              <div className="space-y-8">
                <h3 className="text-2xl font-semibold text-gray-800">
                  Academic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Class <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <BookOpenIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <select
                        name="currentClassId"
                        value={studentData.currentClassId}
                        onChange={handleStudentChange}
                        className={`${getInputClassName("currentClassId")} pl-11`}
                      >
                        <option value="">Select Class</option>
                        {classes.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {formatClassDisplay(cls)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Academic Year
                    </label>
                    <select
                      name="academicYearId"
                      value={studentData.academicYearId}
                      onChange={handleStudentChange}
                      className={getInputClassName("academicYearId")}
                    >
                      <option value="">Select Academic Year</option>
                      {academicYears.map((year) => (
                        <option key={year.id} value={year.id}>
                          {year.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Enrollment Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      name="enrollmentDate"
                      value={studentData.enrollmentDate}
                      onChange={handleStudentChange}
                      className={getInputClassName("enrollmentDate")}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {currentStep === 3 && (
              <div className="space-y-8">
                <h3 className="text-2xl font-semibold text-gray-800">
                  Medical Information
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Medical Conditions
                  </label>
                  <textarea
                    name="medicalConditions"
                    value={studentData.medicalConditions}
                    onChange={handleStudentChange}
                    rows={4}
                    className={getInputClassName("medicalConditions")}
                    placeholder="List any medical conditions..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Allergies
                  </label>
                  <textarea
                    name="allergies"
                    value={studentData.allergies}
                    onChange={handleStudentChange}
                    rows={4}
                    className={getInputClassName("allergies")}
                    placeholder="List any allergies..."
                  />
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {currentStep === 4 && (
              <div className="space-y-8">
                <h3 className="text-2xl font-semibold text-gray-800">
                  Primary Parent / Guardian
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="parentFirstName"
                      value={parentData.parentFirstName}
                      onChange={handleParentChange}
                      className={getInputClassName("parentFirstName")}
                      placeholder="First Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="parentLastName"
                      value={parentData.parentLastName}
                      onChange={handleParentChange}
                      className={getInputClassName("parentLastName")}
                      placeholder="Last Name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Relationship <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="parentRelationship"
                      value={parentData.parentRelationship}
                      onChange={handleParentChange}
                      className={getInputClassName("parentRelationship")}
                    >
                      <option value="">Select relationship</option>
                      <option value="father">Father</option>
                      <option value="mother">Mother</option>
                      <option value="guardian">Guardian</option>
                      <option value="grandparent">Grandparent</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        name="parentPhone"
                        value={parentData.parentPhone}
                        onChange={handleParentChange}
                        className={getInputClassName("parentPhone")}
                        placeholder="+232 76 123 456"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <HomeIcon className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                    <textarea
                      name="parentAddress"
                      value={parentData.parentAddress}
                      onChange={handleParentChange}
                      rows="3"
                      className={`${getInputClassName("parentAddress")} pl-11`}
                      placeholder="Parent full address"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="parentEmail"
                      value={parentData.parentEmail}
                      onChange={handleParentChange}
                      className={getInputClassName("parentEmail")}
                      placeholder="parent@example.com"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 5 */}
            {currentStep === 5 && (
              <div className="space-y-8">
                <h3 className="text-2xl font-semibold text-gray-800">
                  Emergency Contact
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="emergencyName"
                      value={parentData.emergencyName}
                      onChange={handleParentChange}
                      className={getInputClassName("emergencyName")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Relationship <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="emergencyRelationship"
                      value={parentData.emergencyRelationship}
                      onChange={handleParentChange}
                      className={getInputClassName("emergencyRelationship")}
                    >
                      <option value="">Select relationship</option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Sibling</option>
                      <option value="relative">Relative</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="emergencyPhone"
                      value={parentData.emergencyPhone}
                      onChange={handleParentChange}
                      className={getInputClassName("emergencyPhone")}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-between pt-8 border-t border-gray-100">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-8 py-3.5 border border-gray-300 rounded-2xl hover:bg-gray-50 transition w-full sm:w-auto"
                >
                  Previous
                </button>
              )}
              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-10 py-3.5 bg-primary-600 text-white rounded-2xl hover:bg-primary-700 transition w-full sm:w-auto"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-10 py-3.5 bg-green-600 text-white rounded-2xl hover:bg-green-700 transition disabled:opacity-70 w-full sm:w-auto"
                >
                  {loading ? "Updating..." : "Update Student"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}